import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import EmojiPicker from "emoji-picker-react";
import QRCode from "qrcode";
import LanguageSelector from "../../translation/LanguageSelector";
import {
  TranslationProvider,
  useTranslationContext,
} from "../../../context/TranslationContext";
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Input,
  useToast,
  Icon,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Avatar,
  Divider,
  VStack,
  HStack,
  InputGroup,
  InputRightElement,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Image,
} from "@chakra-ui/react";
import {
  FaUser,
  FaUserSecret,
  FaUsers,
  FaUserMinus,
  FaUserShield,
  FaCrown,
  FaCalendarAlt,
  FaInfoCircle,
  FaRegSmile,
  FaSyncAlt,
  FaUserPlus,
  FaReply,
  FaTrash,
  FaTimes,
  FaQrcode,
  FaIdCard,
} from "react-icons/fa";
import { MdTranslate, MdPsychology } from "react-icons/md";
import dayjs from "dayjs";
import axiosInstance from "../../../routes/axiosInstance";
import useSocket from "../../../hooks/useSocket";
import replyAudioFile from "../../../sounds/message_reply_tone.ogg";
import {
  formatTimeOnly,
  formatDateForChatHeader,
} from "../../../logics/timeLogic";
import Sentiment from "sentiment";

const ChatScreen = ({
  space,
  admin,
  currentMember,
  referenceId,
  spaceNameSlug,
}) => {
  return (
    <TranslationProvider currentMember={currentMember}>
      <ChatScreenContent
        space={space}
        admin={admin}
        currentMember={currentMember}
        referenceId={referenceId}
        spaceNameSlug={spaceNameSlug}
      />
    </TranslationProvider>
  );
};

const ChatScreenContent = ({
  space,
  admin,
  currentMember,
  referenceId,
  spaceNameSlug,
}) => {
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMessages, setIsFetchingMessages] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [isLeavingChat, setIsLeavingChat] = useState(false);
  const [translatedMessages, setTranslatedMessages] = useState({}); // Store translations by message ID
  const [translatingMessages, setTranslatingMessages] = useState(new Set()); // Track which messages are being translated
  const [messageSentiments, setMessageSentiments] = useState({}); // Store sentiment analysis by message ID
  const [showSentimentFilter, setShowSentimentFilter] = useState(false); // Toggle sentiment filtering
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isMemberInfoOpen,
    onOpen: onMemberInfoOpen,
    onClose: onMemberInfoClose,
  } = useDisclosure();
  const {
    isOpen: isLeaveConfirmOpen,
    onOpen: onLeaveConfirmOpen,
    onClose: onLeaveConfirmClose,
  } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize WebSocket
  const {
    isConnected,
    typingUsers,
    emitTypingStart,
    emitTypingStop,
    onNewMessage,
    onMessageDeleted,
    offNewMessage,
    offMessageDeleted,
  } = useSocket(currentMember, referenceId);

  // Translation context
  const { translateText, targetLanguage, getLanguageName } =
    useTranslationContext();

  const playReplyMessageTone = () => {
    const newMessageAudio = new Audio(replyAudioFile);
    newMessageAudio.play().catch((error) => {
      console.error("Error playing incoming call sound:", error);
    });
  };

  // Generate QR code for current URL
  const generateQRCode = async () => {
    try {
      const spaceSharableUrl = `${window.location.origin}/s/${referenceId}/${spaceNameSlug}`;
      const qrDataUrl = await QRCode.toDataURL(spaceSharableUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      setQrCodeDataUrl(qrDataUrl);
    } catch (error) {
      console.error("Error generating QR code:", error);
      toast({
        title: "Error generating QR code",
        description: "Failed to create QR code for the current URL",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle member info click
  const handleMemberInfoClick = () => {
    generateQRCode();
    onMemberInfoOpen();
  };

  // Handle leave chat confirmation
  const handleLeaveChat = () => {
    onLeaveConfirmOpen();
  };

  // Handle confirmed leave chat
  const handleConfirmedLeaveChat = async () => {
    setIsLeavingChat(true);
    onLeaveConfirmClose();

    try {
      const response = await axiosInstance.post(
        `/api/spaces/${referenceId}/leave`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            ...(currentMember?.guestAccessId
              ? { Authorization: `Guest ${currentMember.guestAccessId}` }
              : {}),
          },
        },
      );

      if (response.data.success) {
        // Close the modal
        onMemberInfoClose();

        // Remove the local data
        localStorage.removeItem(`member_${referenceId}`);

        // Show success message
        toast({
          title: "Left chat successfully",
          description: "You have left the chat",
          status: "success",
          duration: 2000,
          isClosable: true,
        });

        // Navigate to space preview page
        navigate(`/s/${referenceId}/${spaceNameSlug}`);
      }
    } catch (error) {
      console.error("Error leaving chat:", error);

      const errorMessage =
        error.response?.data?.error || "Failed to leave chat";
      toast({
        title: "Failed to leave chat",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLeavingChat(false);
    }
  };

  // WebSocket message listeners
  useEffect(() => {
    if (!isConnected) return;

    // Listen for new messages
    onNewMessage((messageData) => {
      if (!messageData || !messageData.id) return;
      if (messageData.sender.id === currentMember?._id) return;

      // Create a new message object
      const newMessage = {
        id: messageData.id,
        text:
          messageData.text ||
          (messageData.activity ? messageData.activity.text : ""),
        sender:
          messageData.sender.id === currentMember?._id
            ? "You"
            : messageData.sender.alias,
        ts: new Date(messageData.createdAt),
        senderInfo: messageData.sender,
        type: messageData.type || "text",
        isActivity: messageData.isActivity,
        activity: messageData.activity,
        parentId: messageData.parentId,
        parentMessage: messageData.parentMessage,
      };

      setMessages((prev) => [...prev, newMessage]);
      playReplyMessageTone();
    });

    // Listen for deleted messages
    onMessageDeleted((data) => {
      setMessages((prev) => prev.filter((msg) => msg.id !== data.messageId));
    });

    // Cleanup listeners on unmount
    return () => {
      offNewMessage();
      offMessageDeleted();
    };
  }, [
    isConnected,
    currentMember?._id,
    onNewMessage,
    onMessageDeleted,
    offNewMessage,
    offMessageDeleted,
  ]);

  // Handle typing indicators
  const handleInputChange = (e) => {
    setInput(e.target.value);

    // Emit typing start
    emitTypingStart();

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout to stop typing
    const timeout = setTimeout(() => {
      emitTypingStop();
    }, 1000);

    setTypingTimeout(timeout);
  };

  // Stop typing when component unmounts
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      emitTypingStop();
    };
  }, [typingTimeout, emitTypingStop]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  // Close message actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close selected message if clicking outside the chat area
      if (
        selectedMessage &&
        !event.target.closest("[data-message-id]") &&
        !event.target.closest('[role="dialog"]')
      ) {
        setSelectedMessage(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedMessage]);

  const handleEmojiSelect = (emojiData) => {
    setInput((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Unified function to fetch messages (used for both initial load and refresh)
  const fetchMessages = useCallback(
    async (showRefreshToast = false) => {
      if (showRefreshToast) {
        setIsRefreshing(true);
      } else {
        setIsFetchingMessages(true);
      }

      try {
        const response = await axiosInstance.get(
          `/api/spaces/${referenceId}/chat`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(currentMember?.guestAccessId
                ? { Authorization: `Guest ${currentMember.guestAccessId}` }
                : {}),
            },
          },
        );

        if (response.data.success) {
          const fetchedMessages = response.data.data.messages.map((msg) => ({
            id: msg.id,
            text: msg.text || (msg.activity ? msg.activity.text : ""),
            sender:
              msg.sender.id === currentMember?._id ? "You" : msg.sender.alias,
            ts: new Date(msg.createdAt),
            senderInfo: msg.sender,
            type: msg.type || "text",
            isActivity: msg.isActivity,
            activity: msg.activity,
            parentId: msg.parentId,
            parentMessage: msg.parentMessage,
          }));
          setMessages(fetchedMessages);

          // Handle members data if present (only for admin/owner)
          if (response.data.data.members) {
            setMembers(response.data.data.members);
          }

          if (showRefreshToast) {
            toast({
              title: "Messages refreshed",
              status: "success",
              duration: 1500,
              isClosable: true,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
        toast({
          title: showRefreshToast
            ? "Failed to refresh messages"
            : "Failed to load messages",
          description:
            error.response?.data?.error ||
            (showRefreshToast
              ? "Unable to fetch latest messages"
              : "Unable to fetch chat history"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        if (showRefreshToast) {
          setIsRefreshing(false);
        } else {
          setIsFetchingMessages(false);
        }
      }
    },
    [referenceId, currentMember?.guestAccessId, currentMember?._id, toast],
  );

  const handleRefreshMessages = () => {
    fetchMessages(true); // Call with refresh toast enabled
  };

  // Reply and delete handlers
  const handleReplyToMessage = (message) => {
    setReplyingTo(message);
    setSelectedMessage(null);
    // Focus the input after setting reply state
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleDeleteMessage = async (messageId) => {
    // TODO: Implement delete message API call
    toast({
      title: "Delete functionality",
      description: "Delete message functionality will be implemented",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
    setSelectedMessage(null);
  };

  const handleTranslateMessage = async (message) => {
    try {
      if (targetLanguage === "auto") {
        toast({
          title: "Select Language",
          description:
            "Please select a target language from the translate menu first",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Add message to translating set
      setTranslatingMessages((prev) => new Set(prev).add(message.id));

      const translationResult = await translateText(message.text);

      // Store the translation
      setTranslatedMessages((prev) => ({
        ...prev,
        [message.id]: {
          originalText: message.text,
          translatedText:
            translationResult.translatedText || translationResult.originalText,
          targetLanguage: targetLanguage,
          languageName: getLanguageName(targetLanguage),
          showTranslated: true,
        },
      }));
    } catch (error) {
      toast({
        title: "Translation Failed",
        description: "Failed to translate message. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setTranslatingMessages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(message.id);
        return newSet;
      });
    }
  };

  const toggleTranslation = (messageId) => {
    setTranslatedMessages((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        showTranslated: !prev[messageId]?.showTranslated,
      },
    }));
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleMessageClick = (message, event) => {
    // Don't show actions for activity messages
    if (message.isActivity || message.type === "activity") return;

    // Prevent event bubbling
    event.stopPropagation();

    setSelectedMessage(selectedMessage?.id === message.id ? null : message);
  };

  // Check if current member is admin/owner
  useEffect(() => {
    if (admin?._id && currentMember?._id && admin._id === currentMember._id) {
      setIsAdminLogin(true);
    } else {
      setIsAdminLogin(false);
    }
  }, [admin?._id, currentMember?._id]);

  // Fetch chat messages when component mounts
  useEffect(() => {
    fetchMessages(); // Use the unified function for initial load
  }, [fetchMessages]);

  // Sentiment Analysis Functions
  const analyzeSentiment = useCallback((messageText) => {
    const sentiment = new Sentiment();
    const result = sentiment.analyze(messageText);
    let sentimentType = "neutral";

    // Use comparative score for better accuracy with shorter texts
    // Comparative score is normalized by word count
    if (result.comparative > 0.1) {
      sentimentType = "positive";
    } else if (result.comparative < -0.1) {
      sentimentType = "negative";
    } else if (result.score > 0) {
      sentimentType = "positive";
    } else if (result.score < 0) {
      sentimentType = "negative";
    }

    return {
      score: result.score,
      comparative: result.comparative,
      type: sentimentType,
      words: result.words,
      positive: result.positive,
      negative: result.negative,
    };
  }, []);

  // Analyze sentiment for new messages
  useEffect(() => {
    messages.forEach((message) => {
      if (message.text && !messageSentiments[message.id]) {
        const sentimentResult = analyzeSentiment(message.text);
        console.log("📊 Sentiment Analysis:");
        console.log("  Text:", message.text);
        console.log("  Result:", sentimentResult.type);
        console.log("  Score:", sentimentResult.score);
        console.log("  Comparative:", sentimentResult.comparative);
        console.log("  Positive words:", sentimentResult.positive);
        console.log("  Negative words:", sentimentResult.negative);
        setMessageSentiments((prev) => ({
          ...prev,
          [message.id]: sentimentResult,
        }));
      }
    });
  }, [messages, messageSentiments, analyzeSentiment]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const messageText = input.trim();
    setInput("");
    setIsLoading(true);

    // Stop typing indicator
    emitTypingStop();
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }

    try {
      // Prepare message data
      const messageData = { message: messageText };
      if (replyingTo) {
        messageData.parentMsgId = replyingTo.id;
      }

      // Call the submitChatMessage API
      const response = await axiosInstance.post(
        `/api/spaces/${referenceId}/chat`,
        messageData,
        {
          headers: {
            "Content-Type": "application/json",
            ...(currentMember?.guestAccessId
              ? { Authorization: `Guest ${currentMember.guestAccessId}` }
              : {}),
          },
        },
      );

      if (response.data.success) {
        const newMessage = {
          id: response.data.data.id,
          text: response.data.data.text,
          sender: "You",
          ts: new Date(response.data.data.createdAt),
          senderInfo: response.data.data.sender,
          ...(replyingTo && {
            parentId: replyingTo.id,
            parentMessage: {
              id: replyingTo.id,
              text: replyingTo.text,
              type: replyingTo.type || "text",
              sender: {
                alias: replyingTo.sender,
              },
            },
          }),
        };
        setMessages((msgs) => [...msgs, newMessage]);

        // Clear reply state after sending
        setReplyingTo(null);
      }
    } catch (error) {
      console.error("Error sending message:", error);

      // Add the message back to input field on error
      setInput(messageText);

      const errorMessage =
        error.response?.data?.error || "Failed to send message";
      toast({
        title: "Failed to send message",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      direction="column"
      p={4}
      h="100vh"
      bgGradient="linear(to-br, teal.600, white, teal.50)"
      justify="center"
      align="center"
    >
      {/* Connection Status Indicator */}
      {!isConnected && (
        <Box
          position="fixed"
          top={4}
          right={4}
          bg="red.500"
          color="white"
          px={3}
          py={2}
          borderRadius="md"
          fontSize="sm"
          zIndex={1000}
        >
          Disconnected
        </Box>
      )}

      {/* Content area */}
      <Box
        p={6}
        w="100%"
        maxW="lg"
        h="100%"
        bg="white"
        borderRadius="lg"
        boxShadow="md"
        display="flex"
        flexDirection="column"
      >
        {/* Fixed Header with member info */}
        <Box
          flexShrink={0}
          borderBottom="1px solid"
          borderColor="gray.200"
          pb={4}
          pt={2}
          bg="white"
          boxShadow="sm"
        >
          {/* Top row with title and user info */}
          <Flex justify="space-between" align="center" mb={2}>
            {/* Chat Title - Left side */}
            <Heading
              size="md"
              cursor="pointer"
              onClick={onOpen}
              _hover={{ color: "teal.600" }}
              title="Click to view chat details"
              color="gray.800"
              fontWeight="600"
              noOfLines={1}
              maxW="60%"
            >
              {space?.title || "Chat"}
            </Heading>

            {/* Member info - Right side */}
            <Flex align="center" spacing={3}>
              {/* Language Selector */}
              <LanguageSelector
                size="sm"
                variant="ghost"
                currentMember={currentMember}
              />

              <Flex
                align="center"
                bg="gray.50"
                borderRadius="full"
                px={3}
                py={1}
                cursor="pointer"
                onClick={handleMemberInfoClick}
                _hover={{ bg: "gray.100" }}
                title="Click to view member info and QR code"
              >
                <Icon
                  as={FaUser}
                  boxSize={3}
                  mr={2}
                  color="gray.600"
                  title={
                    currentMember?.isAnon ? "Anonymous User" : "Verified User"
                  }
                />
                <Text
                  fontWeight="500"
                  fontSize="sm"
                  color="gray.700"
                  noOfLines={1}
                  mr={isAdminLogin ? 2 : 0}
                >
                  {currentMember?.name || currentMember?.alias || "You"}
                </Text>
                {isAdminLogin && (
                  <Icon
                    as={FaCrown}
                    color="purple.500"
                    boxSize={3}
                    title="Admin"
                  />
                )}
              </Flex>
            </Flex>
          </Flex>

          {/* Bottom row with refresh button and connection status */}
          <Flex justify="flex-end" align="center">
            {/* Refresh Button */}
            <IconButton
              icon={<Icon as={FaSyncAlt} />}
              size="sm"
              variant="ghost"
              colorScheme="teal"
              onClick={handleRefreshMessages}
              isLoading={isRefreshing}
              aria-label="Refresh messages"
              title="Refresh messages"
              _hover={{ bg: "teal.50" }}
              borderRadius="full"
            />
          </Flex>
        </Box>
        {/* Scrollable Messages Area */}
        <Box
          flex="1"
          bg="gray.50"
          borderRadius="md"
          position="relative"
          overflow="hidden"
          minHeight="0"
        >
          <Box
            ref={messagesEndRef}
            height="100%"
            overflowY="auto"
            padding="16px"
            css={{
              /* Custom scrollbar styling */
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            {isFetchingMessages ? (
              <Flex justify="center" align="center" h="200px">
                <Spinner size="md" color="teal.500" />
                <Text ml={3} color="gray.500">
                  Loading messages...
                </Text>
              </Flex>
            ) : messages.length === 0 ? (
              <Flex justify="center" align="center" h="200px">
                <Text color="gray.400" textAlign="center">
                  No messages yet. Start the conversation!
                </Text>
              </Flex>
            ) : (
              messages.map((msg, i) => {
                // Check if we should show a date header
                const shouldShowDateHeader =
                  i === 0 ||
                  (messages[i - 1] &&
                    new Date(msg.ts).toDateString() !==
                      new Date(messages[i - 1].ts).toDateString());

                // Check if this is an activity message, and choose icon
                if (msg.isActivity || msg.type === "activity") {
                  let ActivityIcon = FaUserPlus;
                  switch (msg.activity?.type) {
                    case "MEMBER_JOIN":
                      ActivityIcon = FaUserPlus;
                      break;
                    case "MEMBER_ADDED":
                      ActivityIcon = FaUserPlus;
                      break;
                    case "MEMBER_REMOVED":
                      ActivityIcon = FaUserMinus;
                      break;
                    case "MEMBER_LEFT":
                      ActivityIcon = FaUserMinus;
                      break;
                    case "SPACE_CREATION":
                      ActivityIcon = FaInfoCircle;
                      break;
                    default:
                      ActivityIcon = FaUserPlus;
                  }

                  const activityText = msg.activity?.text || msg.text;
                  return (
                    <React.Fragment key={msg.id || i}>
                      {/* Date Header */}
                      {shouldShowDateHeader && (
                        <Flex justify="center" mb={4} mt={i === 0 ? 0 : 4}>
                          <Box
                            bg="white"
                            color="gray.600"
                            px={3}
                            py={1}
                            borderRadius="full"
                            boxShadow="sm"
                            border="1px solid"
                            borderColor="gray.200"
                            fontSize="xs"
                            fontWeight="medium"
                          >
                            {formatDateForChatHeader(msg.ts)}
                          </Box>
                        </Flex>
                      )}

                      <Flex justify="center" mb={3}>
                        <Box
                          bg="gray.100"
                          borderRadius="full"
                          px={3}
                          py={1}
                          maxW="80%"
                        >
                          <Flex align="center" justify="center">
                            <Icon
                              as={ActivityIcon}
                              color="gray.500"
                              boxSize={3}
                              mr={2}
                            />
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              textAlign="center"
                              fontStyle="italic"
                            >
                              {activityText}
                            </Text>
                          </Flex>
                        </Box>
                      </Flex>
                    </React.Fragment>
                  );
                }

                // Regular message rendering
                const isCurrentUser = msg.sender === "You";
                const isSelected = selectedMessage?.id === msg.id;

                return (
                  <Box key={msg.id || i} mb={3} data-message-id={msg.id}>
                    {/* Date Header for regular messages */}
                    {shouldShowDateHeader && (
                      <Flex justify="center" mb={4} mt={i === 0 ? 0 : 4}>
                        <Box
                          bg="white"
                          color="gray.600"
                          px={3}
                          py={1}
                          borderRadius="full"
                          boxShadow="sm"
                          border="1px solid"
                          borderColor="gray.200"
                          fontSize="xs"
                          fontWeight="medium"
                        >
                          {formatDateForChatHeader(msg.ts)}
                        </Box>
                      </Flex>
                    )}

                    <Flex justify={isCurrentUser ? "flex-end" : "flex-start"}>
                      <Popover
                        isOpen={isSelected}
                        onClose={() => setSelectedMessage(null)}
                        placement={isCurrentUser ? "left-start" : "right-start"}
                        closeOnBlur={true}
                        closeOnEsc={true}
                      >
                        <PopoverTrigger>
                          <Box
                            maxW="70%"
                            bg={isCurrentUser ? "teal.100" : "white"}
                            borderRadius="lg"
                            p={3}
                            boxShadow="sm"
                            border={isCurrentUser ? "none" : "1px solid"}
                            borderColor={
                              isCurrentUser ? "transparent" : "gray.200"
                            }
                            cursor="pointer"
                            onClick={(e) => handleMessageClick(msg, e)}
                            position="relative"
                            _hover={{
                              boxShadow: "md",
                              transform: "translateY(-1px)",
                            }}
                            transition="all 0.2s"
                            borderBottom={
                              messageSentiments[msg.id] && showSentimentFilter
                                ? "3px solid"
                                : "none"
                            }
                            borderBottomColor={
                              messageSentiments[msg.id] && showSentimentFilter
                                ? messageSentiments[msg.id].type === "positive"
                                  ? "green.400"
                                  : messageSentiments[msg.id].type ===
                                      "negative"
                                    ? "red.400"
                                    : "gray.400"
                                : "transparent"
                            }
                          >
                            {/* Reply indicator if this message is replying to another */}
                            {msg.parentMessage && (
                              <Box
                                bg="gray.100"
                                borderRadius="md"
                                p={2}
                                mb={2}
                                borderLeft="3px solid"
                                borderColor="teal.500"
                                cursor="pointer"
                                _hover={{ bg: "gray.150" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Scroll to the parent message
                                  const parentElement = document.querySelector(
                                    `[data-message-id="${msg.parentMessage.id}"]`,
                                  );
                                  if (parentElement) {
                                    parentElement.scrollIntoView({
                                      behavior: "smooth",
                                      block: "center",
                                    });
                                    // Briefly highlight the parent message
                                    parentElement.style.backgroundColor =
                                      "#E6FFFA";
                                    setTimeout(() => {
                                      parentElement.style.backgroundColor = "";
                                    }, 2000);
                                  }
                                }}
                              >
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  fontWeight="bold"
                                >
                                  Reply to{" "}
                                  {msg.parentMessage.sender.alias ===
                                  currentMember?.alias
                                    ? "You"
                                    : msg.parentMessage.sender.alias}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  noOfLines={1}
                                >
                                  {msg.parentMessage.text}
                                </Text>
                              </Box>
                            )}

                            {!isCurrentUser && (
                              <Flex align="center" mb={1}>
                                {msg.senderInfo?.isAnon && (
                                  <Icon
                                    as={FaUserSecret}
                                    color="gray.500"
                                    boxSize={3}
                                    mr={1}
                                    title="Anonymous User"
                                  />
                                )}
                                <Text
                                  fontWeight="bold"
                                  fontSize="xs"
                                  color="gray.600"
                                >
                                  {msg.sender}
                                </Text>
                              </Flex>
                            )}

                            {/* Message Text */}
                            <Box>
                              <Text fontSize="sm" color="gray.800">
                                {(() => {
                                  const translation =
                                    translatedMessages[msg.id];
                                  if (
                                    translation &&
                                    translation.showTranslated !== false &&
                                    translation.translatedText
                                  ) {
                                    return translation.translatedText;
                                  }
                                  return msg.text;
                                })()}
                              </Text>

                              {/* Translation indicator */}
                              {translatedMessages[msg.id] && (
                                <HStack spacing={2} mt={1}>
                                  <Badge
                                    size="xs"
                                    colorScheme="teal"
                                    variant="subtle"
                                    cursor="pointer"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent popup from opening
                                      toggleTranslation(msg.id);
                                    }}
                                  >
                                    <HStack spacing={1}>
                                      <Icon as={MdTranslate} boxSize={2} />
                                      <Text fontSize="xs">
                                        {translatedMessages[msg.id]
                                          ?.showTranslated === false
                                          ? "Show Translation"
                                          : "Show Original"}
                                      </Text>
                                    </HStack>
                                  </Badge>
                                  <Text fontSize="xs" color="gray.500">
                                    {translatedMessages[msg.id]?.languageName}
                                  </Text>
                                </HStack>
                              )}

                              {/* Loading indicator for translating messages */}
                              {translatingMessages.has(msg.id) && (
                                <HStack spacing={2} mt={1}>
                                  <Spinner size="xs" color="teal.500" />
                                  <Text fontSize="xs" color="gray.500">
                                    Translating...
                                  </Text>
                                </HStack>
                              )}
                            </Box>

                            <Text
                              fontSize="xs"
                              color="gray.500"
                              mt={1}
                              textAlign="right"
                            >
                              {formatTimeOnly(msg.ts)}
                            </Text>
                          </Box>
                        </PopoverTrigger>

                        <PopoverContent
                          width="auto"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <PopoverArrow />
                          <PopoverBody p={2}>
                            <VStack spacing={1} align="stretch">
                              <IconButton
                                icon={<Icon as={FaReply} boxSize={3} />}
                                size="sm"
                                variant="ghost"
                                onClick={() => handleReplyToMessage(msg)}
                                _hover={{ bg: "blue.50", color: "blue.600" }}
                                aria-label="Reply to message"
                                title="Reply"
                              />
                              {targetLanguage !== "auto" ? (
                                <IconButton
                                  icon={<Icon as={MdTranslate} boxSize={3} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    handleTranslateMessage(msg);
                                    setSelectedMessage(null); // Close the popup
                                  }}
                                  _hover={{ bg: "teal.50", color: "teal.600" }}
                                  aria-label="Translate message"
                                  title={`Translate to ${getLanguageName(
                                    targetLanguage,
                                  )}`}
                                />
                              ) : (
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  p={2}
                                  textAlign="center"
                                >
                                  NA
                                </Text>
                              )}
                              {(isCurrentUser || isAdminLogin) && (
                                <IconButton
                                  icon={<Icon as={FaTrash} boxSize={3} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  _hover={{ bg: "red.50", color: "red.600" }}
                                  aria-label="Delete message"
                                  title="Delete"
                                />
                              )}
                            </VStack>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </Flex>
                  </Box>
                );
              })
            )}

            {/* Typing Indicator */}
            {typingUsers.length > 0 && (
              <Box mb={3}>
                <Flex justify="flex-start">
                  {/* <Box
                    bg="gray.200"
                    borderRadius="lg"
                    p={3}
                    maxW="70%"
                    opacity={1}
                    minHh="500px"
                    transition="opacity 0.3s ease-in-out"
                  > */}
                  <Text fontSize="sm" fontStyle="italic">
                    {typingUsers.length === 1
                      ? `${typingUsers[0].memberAlias} is typing...`
                      : `${typingUsers.length} people are typing...`}
                  </Text>
                  {/* </Box> */}
                </Flex>
              </Box>
            )}
          </Box>
        </Box>

        {/* Fixed Input Area */}
        <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200" pt={4}>
          {/* Reply Preview */}
          {replyingTo && (
            <Box
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              mb={3}
              borderLeft="3px solid"
              borderLeftColor="teal.500"
            >
              <Flex justify="space-between" align="flex-start">
                <Box flex={1}>
                  <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>
                    Replying to {replyingTo.sender}
                  </Text>
                  <Text fontSize="sm" color="gray.700" noOfLines={2}>
                    {replyingTo.text}
                  </Text>
                </Box>
                <IconButton
                  icon={<Icon as={FaTimes} />}
                  size="xs"
                  variant="ghost"
                  onClick={handleCancelReply}
                  aria-label="Cancel reply"
                  ml={2}
                />
              </Flex>
            </Box>
          )}

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <Box
              mb={3}
              position="relative"
              display="flex"
              justifyContent="flex-end"
            >
              <Box
                position="absolute"
                bottom="0"
                right="0"
                zIndex={10}
                bg="white"
                borderRadius="md"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.200"
              >
                <EmojiPicker
                  onEmojiClick={handleEmojiSelect}
                  width={350}
                  height={400}
                  previewConfig={{
                    showPreview: false,
                  }}
                  skinTonesDisabled
                  searchDisabled={false}
                />
              </Box>
            </Box>
          )}

          <Flex>
            <InputGroup mr={2}>
              <Input
                ref={inputRef}
                placeholder={
                  replyingTo
                    ? `Replying to ${replyingTo.sender}...`
                    : "Type a message..."
                }
                value={input}
                // onChange={(e) => setInput(e.target.value)}
                onChange={handleInputChange}
                onKeyPress={(e) =>
                  e.key === "Enter" && !isLoading && handleSendMessage()
                }
                isDisabled={isLoading}
                bg="white"
                _focus={{
                  borderColor: "teal.500",
                  boxShadow: "0 0 0 1px teal.500",
                }}
                pr="40px"
              />
              <InputRightElement>
                <IconButton
                  icon={<Icon as={FaRegSmile} boxSize={5} />}
                  size="lg"
                  variant="ghost"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  aria-label="Toggle emoji picker"
                  _hover={{ bg: "transparent" }}
                />
              </InputRightElement>
            </InputGroup>
            <Button
              colorScheme="teal"
              onClick={handleSendMessage}
              isLoading={isLoading}
              loadingText="Sending..."
              isDisabled={!input.trim() || isLoading}
              minW="80px"
            >
              Send
            </Button>
          </Flex>
        </Box>
      </Box>

      {/* Member Info Modal with QR Code */}
      <Modal isOpen={isMemberInfoOpen} onClose={onMemberInfoClose} size="md">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent mx={4} my={8} p={4}>
          <ModalHeader pb={2} px={0} fontSize="lg" fontWeight="semibold">
            <Flex align="center">
              <Icon as={FaIdCard} boxSize={5} mr={2} color="teal.500" />
              Member Information
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody p={0}>
            <VStack spacing={6} align="stretch">
              {/* Member Details Section */}
              <Box p={4} bg="gray.50" borderRadius="lg">
                <VStack spacing={3} align="center">
                  <Avatar
                    size="lg"
                    bg="teal.500"
                    color="white"
                    icon={<FaUser />}
                  />
                  <VStack spacing={1} textAlign="center">
                    <Text fontSize="lg" fontWeight="600" color="gray.800">
                      {currentMember?.name || currentMember?.alias || "You"}
                    </Text>
                    <HStack spacing={2}>
                      <Icon
                        as={currentMember?.isAnon ? FaUserSecret : FaUserShield}
                        boxSize={3}
                        color="gray.500"
                      />
                      <Text fontSize="sm" color="gray.600">
                        {currentMember?.isAnon
                          ? "Anonymous User"
                          : "Verified User"}
                      </Text>
                      {isAdminLogin && (
                        <>
                          <Icon as={FaCrown} boxSize={3} color="purple.500" />
                          <Text
                            fontSize="sm"
                            color="purple.600"
                            fontWeight="500"
                          >
                            Admin
                          </Text>
                        </>
                      )}
                    </HStack>
                  </VStack>
                </VStack>
              </Box>

              {/* Sentiment Analysis Section */}
              <Box p={4} bg="gray.50" borderRadius="lg">
                <HStack justify="space-between" align="flex-start">
                  <VStack spacing={1} align="stretch" flex={1}>
                    <HStack spacing={2}>
                      <Icon as={MdPsychology} boxSize={4} color="teal.500" />
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        Sentiment Analysis
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" lineHeight="1.3">
                      Shows colored borders:{" "}
                      <Text as="span" color="green.500" fontWeight="500">
                        Green
                      </Text>{" "}
                      (positive),{" "}
                      <Text as="span" color="red.500" fontWeight="500">
                        Red
                      </Text>{" "}
                      (negative),{" "}
                      <Text as="span" color="gray.500" fontWeight="500">
                        Gray
                      </Text>{" "}
                      (neutral). Note: AI analysis is not 100% accurate.
                    </Text>
                  </VStack>
                  <Box
                    display="flex"
                    alignItems="center"
                    cursor="pointer"
                    ml={3}
                    onClick={() => setShowSentimentFilter(!showSentimentFilter)}
                  >
                    <Box position="relative">
                      <Box
                        w="44px"
                        h="24px"
                        bg={showSentimentFilter ? "teal.500" : "gray.300"}
                        borderRadius="full"
                        transition="background-color 0.2s"
                      />
                      <Box
                        position="absolute"
                        top="2px"
                        left={showSentimentFilter ? "22px" : "2px"}
                        w="20px"
                        h="20px"
                        bg="white"
                        borderRadius="full"
                        transition="left 0.2s"
                        boxShadow="sm"
                      />
                    </Box>
                  </Box>
                </HStack>
              </Box>

              {/* QR Code Section */}
              <Box
                p={4}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
              >
                <VStack spacing={3} align="center">
                  <HStack spacing={2}>
                    <Icon as={FaQrcode} boxSize={4} color="teal.500" />
                    <Text fontSize="sm" fontWeight="600" color="gray.700">
                      Share Chat URL
                    </Text>
                  </HStack>
                  {qrCodeDataUrl ? (
                    <Box
                      p={3}
                      bg="white"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Image
                        src={qrCodeDataUrl}
                        alt="QR Code for chat URL"
                        maxW="200px"
                        maxH="200px"
                      />
                    </Box>
                  ) : (
                    <Spinner size="md" color="teal.500" />
                  )}
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    Scan this QR code and enter the code{" "}
                    {currentMember?.guestAccessId && (
                      <Text as="span" fontWeight="bold" color="gray.700">
                        {currentMember.guestAccessId}
                      </Text>
                    )}{" "}
                    to join from other device
                  </Text>
                </VStack>
              </Box>

              {/* Exit Chat Button - Only show for non-admin members */}
              {!isAdminLogin && (
                <Box p={4}>
                  <Button
                    colorScheme="red"
                    variant="outline"
                    size="md"
                    width="100%"
                    onClick={handleLeaveChat}
                    _hover={{
                      bg: "red.50",
                      borderColor: "red.400",
                    }}
                  >
                    Leave Chat
                  </Button>
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Leave Chat Confirmation Modal */}
      <Modal
        isOpen={isLeaveConfirmOpen}
        onClose={onLeaveConfirmClose}
        size="md"
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent mx={4} my={8}>
          <ModalHeader fontSize="lg" fontWeight="semibold" color="red.600">
            <Flex align="center">
              <Icon as={FaTimes} boxSize={5} mr={2} />
              Leave Chat
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" color="gray.700" lineHeight="1.6">
                If you leave this chat, you will{" "}
                <Text as="span" fontWeight="bold" color="red.600">
                  not be able to join again
                </Text>{" "}
                even with the access code.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Are you sure you want to leave this chat permanently?
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onLeaveConfirmClose}
              _hover={{ bg: "gray.100" }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              colorScheme="red"
              onClick={handleConfirmedLeaveChat}
              isLoading={isLeavingChat}
              loadingText="Leaving..."
              _hover={{
                bg: "red.50",
                borderColor: "red.400",
              }}
            >
              Leave Chat
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Chat Details Modal - WhatsApp Style */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          mx={4}
          my={8}
          p={4}
          maxH="80vh"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader
            pb={2}
            px={0}
            fontSize="lg"
            fontWeight="semibold"
            borderBottom="1px solid"
            borderColor="gray.100"
            flexShrink={0}
          >
            Chat Info
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody
            p={0}
            flex="1"
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            {/* Header Section with Avatar and Title */}
            <VStack spacing={0} align="stretch">
              {/* Profile Section */}
              <Box p={6} textAlign="center" bg="gray.50">
                <Avatar
                  size="xl"
                  bg="teal.500"
                  color="white"
                  mb={3}
                  icon={<FaUsers />}
                />
                <Heading size="md" mb={1} color="gray.800">
                  {space?.title || "Untitled Chat"}
                </Heading>
                {space?.description && (
                  <Text fontSize="sm" color="gray.600" mx="auto">
                    {space.description}
                  </Text>
                )}
              </Box>

              <Divider />

              {/* Details List */}
              <VStack spacing={0} align="stretch">
                {/* Admin Info */}
                <HStack p={4} spacing={3} _hover={{ bg: "gray.50" }}>
                  <Icon as={FaCrown} boxSize={5} color="gray.500" />
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                      Admin
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.600">
                        {isAdminLogin
                          ? `${admin?.name || admin?.alias} (You)`
                          : admin?.name || admin?.alias || "Unknown User"}
                      </Text>
                      <Icon
                        as={space?.isIncognito ? FaUserSecret : FaUserShield}
                        boxSize={3}
                        color="gray.500"
                        title={
                          admin?.isAnon ? "Anonymous Name" : "Public Admin"
                        }
                      />
                    </HStack>
                  </Box>
                </HStack>

                <Divider />

                {/* Members Count */}
                {!isAdminLogin && (
                  <>
                    <HStack p={4} spacing={3} _hover={{ bg: "gray.50" }}>
                      <Icon as={FaUsers} boxSize={5} color="gray.500" />
                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.800"
                        >
                          Members
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {space?.memberCount + 1 || "1"} member
                          {(space?.memberCount + 1 || 1) !== 1 ? "s" : ""}
                        </Text>
                      </Box>
                    </HStack>
                    <Divider />
                  </>
                )}

                {/* <Divider /> */}

                {/* Created Date */}
                {space?.createdAt && (
                  <>
                    <HStack p={4} spacing={3} _hover={{ bg: "gray.50" }}>
                      <Icon as={FaCalendarAlt} boxSize={5} color="gray.500" />
                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.800"
                        >
                          Created
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {dayjs(space.createdAt).format(
                            "MMMM D, YYYY [at] h:mm A",
                          )}
                        </Text>
                      </Box>
                    </HStack>
                    <Divider />
                  </>
                )}

                {/* Space Type */}
                <HStack p={4} spacing={3} _hover={{ bg: "gray.50" }}>
                  <Icon as={FaInfoCircle} boxSize={5} color="gray.500" />
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                      Chat Type
                    </Text>
                    <HStack spacing={2}>
                      <Badge
                        colorScheme={
                          space?.participantMode === "ANONYMOUS"
                            ? "purple"
                            : "blue"
                        }
                        size="sm"
                        variant="subtle"
                      >
                        {space?.participantMode === "ANONYMOUS"
                          ? "ANONYMOUS"
                          : "AUTHENTICATED"}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">
                        {space?.participantMode === "ANONYMOUS"
                          ? "Anonymous members"
                          : "Verified members"}
                      </Text>
                    </HStack>
                  </Box>
                </HStack>

                {/* Members List - Only show for admin/owner */}
                {isAdminLogin && members && members.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <HStack p={4} spacing={3}>
                        <Icon as={FaUsers} boxSize={5} color="gray.500" />
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.800"
                        >
                          Members ({members.length})
                        </Text>
                      </HStack>
                      <VStack spacing={0} align="stretch">
                        {members.map((memberItem, index) => (
                          <HStack
                            key={memberItem.id || index}
                            p={2}
                            pl={10}
                            spacing={3}
                            _hover={{ bg: "gray.50" }}
                            borderLeft="1px solid"
                            borderColor="gray.100"
                          >
                            <Avatar
                              size="sm"
                              bg={
                                memberItem.isOwner ? "purple.500" : "teal.500"
                              }
                              color="white"
                              icon={
                                memberItem.isOwner ? (
                                  <FaCrown />
                                ) : memberItem.isAnon ? (
                                  <FaUserSecret />
                                ) : (
                                  <FaUserShield />
                                )
                              }
                            />
                            <Box flex={1}>
                              <HStack spacing={2} align="center">
                                <Text
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color="gray.800"
                                >
                                  {memberItem.alias}
                                  {memberItem.id === currentMember?._id
                                    ? " (You)"
                                    : ""}
                                </Text>
                                <Icon
                                  as={
                                    memberItem.isAnon
                                      ? FaUserSecret
                                      : FaUserShield
                                  }
                                  boxSize={3}
                                  color="gray.500"
                                  title={
                                    memberItem.isAnon
                                      ? "Anonymous User"
                                      : "Verified User"
                                  }
                                />
                              </HStack>
                              <Text fontSize="xs" color="gray.500">
                                Joined{" "}
                                {dayjs(memberItem.joinedAt).format(
                                  "MMM D, YYYY",
                                )}
                              </Text>
                            </Box>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </>
                )}
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default ChatScreen;
